import { useCallback, useEffect, useState } from 'react'
import { userRepository } from '@/data/users/repositories/user_repository_impl'
import type { UserDetail } from '@/domain/users/entities/user.entity'
import { GetUserByIdUseCase } from '@/domain/users/usecases/get_user_by_id.usecase'
import { toAppError } from '@/core/errors/app_error'
import { logError } from '@/utils/logger'
const defaultGetUserById = new GetUserByIdUseCase(userRepository)

export interface UseUserDetailViewModelDeps {
  getUserById?: GetUserByIdUseCase
}

export function useUserDetailViewModel(
  userId: string | undefined,
  deps: UseUserDetailViewModelDeps = {}
) {
  const getUserById = deps.getUserById ?? defaultGetUserById
  const [user, setUser] = useState<UserDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!userId) {
      setError('User ID is required')
      setUser(null)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      const detail = await getUserById.execute(userId)
      setUser(detail)
    } catch (err: unknown) {
      logError('Failed to fetch user', err)
      setError(toAppError(err, 'Failed to load user').message)
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [getUserById, userId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return {
    state: { user },
    isLoading,
    error,
    actions: { refresh },
  }
}
